import { useState, useCallback, useRef, useEffect } from "react";
import { Navbar } from "../components/Navbar";
import { ParserPanel } from "../components/ParserPanel";
import { BulkPanel } from "../components/BulkPanel";
import { LabelControls } from "../components/LabelControls";
import { ThermalLabel } from "../components/ThermalLabel";
import { PricingSection } from "../components/PricingSection";
import { StoreDialog } from "../components/StoreDialog";
import { HistoryDialog } from "../components/HistoryDialog";
import { ScanValidateDialog } from "../components/ScanValidateDialog";
import { AdminDialog } from "../components/AdminDialog";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useDarkMode } from "../hooks/useDarkMode";
import { emptyLabel } from "../lib/regexParser";
import { exportLabelsToPdf } from "../lib/pdf";
import { api } from "../lib/api";
import { Printer, FileDown, Sparkles, FileSpreadsheet, ChevronDown, Zap, ScanLine } from "lucide-react";

const ANON_KEY = "resicetak_anon_usage";
const FREE_LIMIT = 10;

const nextPaint = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
const today = () => new Date().toISOString().slice(0, 10);

const defaultControls = {
  paperSize: "100x150",
  showSender: true,
  showLogo: false,
  showItems: true,
  showCod: false,
  showBarcode: true,
  showQR: true,
  showFragile: false,
};

export default function MainApp() {
  const { user, isPro, isAdmin, refresh } = useAuth();
  const [tab, setTab] = useState("parser");
  const [singleLabel, setSingleLabel] = useState(emptyLabel());
  const [bulkRows, setBulkRows] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [controls, setControls] = useState(defaultControls);
  const [defaultCourier, setDefaultCourier] = useState("none");
  const [store, setStore] = useLocalStorage("resicetak_store", {
    storeName: "", senderName: "", senderPhone: "", senderAddress: "", logo: "",
  });
  const [showPricing, setShowPricing] = useState(false);
  const [showStore, setShowStore] = useState(false);
  const [jobLabels, setJobLabels] = useState([]);
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useLocalStorage("resicetak_templates", []);
  const [config, setConfig] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [dark, toggleDark] = useDarkMode();
  const jobRefs = useRef({});

  const withCourier = useCallback((l) => ({
    ...l,
    courier: l.courier || (defaultCourier !== "none" ? defaultCourier : ""),
  }), [defaultCourier]);

  const previewLabel = withCourier(tab === "parser" ? singleLabel : (bulkRows[activeIndex] || emptyLabel()));

  const loadUsage = useCallback(async () => {
    if (isPro) { setUsage({ limit: null, remaining: null }); return; }
    if (user) {
      try {
        const { data } = await api.get("/me/usage");
        setUsage({ limit: data.limit, remaining: data.remaining });
      } catch { /* ignore */ }
    } else {
      let a = { date: today(), count: 0 };
      try { a = JSON.parse(localStorage.getItem(ANON_KEY)) || a; } catch { /* ignore */ }
      if (a.date !== today()) a = { date: today(), count: 0 };
      const lim = (config && config.free_daily_limit) || FREE_LIMIT;
      setUsage({ limit: lim, remaining: Math.max(0, lim - a.count) });
    }
  }, [user, isPro, config]);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  useEffect(() => { api.get("/config").then(({ data }) => setConfig(data)).catch(() => {}); }, []);

  const loadHistory = useCallback(async () => {
    if (user) {
      try { const { data } = await api.get("/history"); setHistory(data); } catch { /* ignore */ }
    } else {
      try { setHistory(JSON.parse(localStorage.getItem("resicetak_history_anon")) || []); } catch { setHistory([]); }
    }
  }, [user]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const persistAnon = (list) => { try { localStorage.setItem("resicetak_history_anon", JSON.stringify(list)); } catch { /* ignore */ } };

  const applyTemplate = (t) => { setControls(t.controls); setDefaultCourier(t.defaultCourier || "none"); toast.success(`Template "${t.name}" diterapkan`); };
  const saveTemplate = (name) => {
    const t = { id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()), name, controls, defaultCourier };
    setTemplates((prev) => [...prev, t]);
    toast.success("Template disimpan");
  };
  const deleteTemplate = (id) => setTemplates((prev) => prev.filter((t) => t.id !== id));

  const consume = async (n) => {
    if (isPro) return true;
    if (user) {
      try {
        const { data } = await api.post("/me/usage/increment", { count: n });
        setUsage({ limit: data.limit, remaining: data.remaining });
        return true;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Batas cetak tercapai");
        setShowPricing(true);
        return false;
      }
    }
    // anonymous
    const lim = (config && config.free_daily_limit) || FREE_LIMIT;
    let a = { date: today(), count: 0 };
    try { a = JSON.parse(localStorage.getItem(ANON_KEY)) || a; } catch { /* ignore */ }
    if (a.date !== today()) a = { date: today(), count: 0 };
    if (a.count + n > lim) {
      toast.error(`Batas ${lim} resi/hari (gratis) tercapai. Masuk & Upgrade Pro untuk unlimited.`);
      setShowPricing(true);
      return false;
    }
    a.count += n;
    localStorage.setItem(ANON_KEY, JSON.stringify(a));
    setUsage({ limit: lim, remaining: Math.max(0, lim - a.count) });
    return true;
  };

  const getOutputLabels = () => {
    if (tab === "parser") {
      if (!singleLabel.recipient_name && !singleLabel.address) {
        toast.error("Isi data penerima dulu ya");
        return [];
      }
      return [withCourier(singleLabel)];
    }
    if (!bulkRows.length) { toast.error("Upload file CSV/Excel dulu"); return []; }
    const valid = bulkRows.filter((r) => r.address && r.phone);
    const skipped = bulkRows.length - valid.length;
    if (!valid.length) { toast.error("Semua baris belum lengkap (butuh Alamat & No HP)"); return []; }
    if (skipped > 0) toast.warning(`${skipped} baris dilewati karena Alamat/HP kosong`);
    return valid.map(withCourier);
  };

  const outputJob = async (list, action, layout) => {
    const ok = await consume(list.length);
    if (!ok) return false;
    setJobLabels(list);
    await nextPaint();
    await nextPaint();
    if (action === "print") {
      window.print();
      return true;
    }
    const nodes = list.map((_, i) => jobRefs.current[i]).filter(Boolean);
    try {
      await exportLabelsToPdf(nodes, controls.paperSize, layout);
      toast.success("PDF thermal siap!");
      return true;
    } catch {
      toast.error("Gagal membuat PDF");
      return false;
    }
  };

  const recordHistory = async (list) => {
    if (user) {
      try { await api.post("/history", { items: list }); await loadHistory(); } catch { /* ignore */ }
      return;
    }
    const at = new Date().toISOString();
    const entries = list.map((l) => ({
      id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now() + Math.random()),
      at, label: l,
    }));
    setHistory((prev) => { const nx = [...entries, ...prev].slice(0, 200); persistAnon(nx); return nx; });
  };

  const deleteHistoryItem = async (id) => {
    if (user) { try { await api.delete(`/history/${id}`); } catch { /* ignore */ } setHistory((p) => p.filter((x) => x.id !== id)); }
    else setHistory((p) => { const nx = p.filter((x) => x.id !== id); persistAnon(nx); return nx; });
  };
  const clearHistory = async () => {
    if (user) { try { await api.delete("/history"); } catch { /* ignore */ } }
    else persistAnon([]);
    setHistory([]);
  };

  const runJob = async (action, layout) => {
    const list = getOutputLabels();
    if (!list.length) return;
    const ok = await outputJob(list, action, layout);
    if (ok) recordHistory(list);
  };

  const reprintFromHistory = async (label) => {
    setShowHistory(false);
    await new Promise((r) => setTimeout(r, 150));
    await outputJob([withCourier(label)], "print");
  };

  const loadFromHistory = (label) => {
    setSingleLabel(label);
    setTab("parser");
    setShowHistory(false);
    toast.success("Resi dimuat ke editor");
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <Navbar
        onOpenStore={() => setShowStore(true)}
        onOpenPricing={() => setShowPricing(true)}
        onOpenHistory={() => setShowHistory(true)}
        onOpenAdmin={() => setShowAdmin(true)}
        usage={usage}
        isDark={dark}
        onToggleDark={toggleDark}
        isAdmin={isAdmin}
      />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-mono font-semibold text-sky-700 uppercase tracking-wide">
            <Zap size={13} /> Tanpa install • Siap cetak thermal
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-none"
            style={{ fontFamily: "'Outfit', sans-serif" }}>
            Ubah chat WhatsApp jadi <span className="text-sky-600">label resi</span> rapi dalam detik.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Tempel teks alamat, biarkan AI merapikan, lalu cetak langsung ke printer thermal atau ekspor PDF ber-barcode. Khusus untuk seller olshop Indonesia.
          </p>
        </div>
      </section>

      {/* Workspace */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-[1fr_380px] gap-6 lg:gap-8 items-start">
          {/* Left: inputs */}
          <div className="space-y-6">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-2 max-w-md">
                <TabsTrigger value="parser" data-testid="nav-parser-tab" className="gap-1.5">
                  <Sparkles size={15} /> Teks WA
                </TabsTrigger>
                <TabsTrigger value="bulk" data-testid="nav-bulk-tab" className="gap-1.5">
                  <FileSpreadsheet size={15} /> Bulk CSV/Excel
                </TabsTrigger>
              </TabsList>
              <TabsContent value="parser" className="mt-5">
                <ParserPanel label={singleLabel} setLabel={setSingleLabel} />
              </TabsContent>
              <TabsContent value="bulk" className="mt-5">
                <BulkPanel rows={bulkRows} setRows={setBulkRows} activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex} isPro={isPro} />
              </TabsContent>
            </Tabs>

            <LabelControls
              controls={controls} setControls={setControls}
              defaultCourier={defaultCourier} setDefaultCourier={setDefaultCourier}
              isPro={isPro} onProToggle={() => setShowPricing(true)}
              templates={templates} onApplyTemplate={applyTemplate}
              onSaveTemplate={saveTemplate} onDeleteTemplate={deleteTemplate}
            />
          </div>

          {/* Right: live preview */}
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-widest font-mono font-semibold text-slate-500">Live Preview</span>
                {tab === "bulk" && bulkRows.length > 0 && (
                  <span className="text-xs text-slate-400">#{activeIndex + 1} dari {bulkRows.length}</span>
                )}
              </div>
              <div className="flex justify-center overflow-auto rounded-lg bg-slate-100 p-4" style={{ maxHeight: 620 }}>
                <div className="shadow-lg">
                  <ThermalLabel data={previewLabel} controls={controls} store={store} isPro={isPro} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button data-testid="btn-direct-thermal-print" onClick={() => runJob("print")}
                className="bg-slate-900 text-white hover:bg-slate-800 gap-2">
                <Printer size={16} /> Cetak
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="btn-export-pdf-grid" variant="outline" className="gap-1.5 w-full border-sky-300 text-sky-700">
                    <FileDown size={16} /> Export PDF <ChevronDown size={14} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid="pdf-layout-single" onClick={() => runJob("pdf", "single")}>
                    1 label / halaman (ukuran asli)
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="pdf-layout-a4" onClick={() => runJob("pdf", "a4")}>
                    Grid di kertas A4
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button data-testid="btn-scan-validate" variant="ghost" onClick={() => setShowScan(true)}
              className="w-full gap-2 text-sky-700 hover:text-sky-800">
              <ScanLine size={16} /> Tes Scan & Validasi Barcode
            </Button>
            <p className="text-xs text-center text-slate-400">
              Tip: di dialog cetak browser, atur Margin = None & Scale = 100% untuk hasil presisi.
            </p>
          </div>
        </div>
      </main>

      {/* Hidden print / export area */}
      <div id="print-area" aria-hidden>
        {jobLabels.map((l, i) => (
          <ThermalLabel key={i} idx={i} data={l} controls={controls} store={store} isPro={isPro}
            forwardRef={(el) => { jobRefs.current[i] = el; }} />
        ))}
      </div>

      <PricingSection open={showPricing} onClose={() => { setShowPricing(false); refresh(); loadUsage(); }}
        proPrice={config?.pro_price} freeLimit={config?.free_daily_limit} />
      <StoreDialog open={showStore} onClose={() => setShowStore(false)} store={store} setStore={setStore}
        onOpenPricing={() => setShowPricing(true)} />
      <HistoryDialog open={showHistory} onClose={() => setShowHistory(false)} history={history}
        onDelete={deleteHistoryItem} onClear={clearHistory} onReprint={reprintFromHistory} onLoad={loadFromHistory} />
      <ScanValidateDialog open={showScan} onClose={() => setShowScan(false)}
        value={previewLabel.code || previewLabel.phone} />
      <AdminDialog open={showAdmin} onClose={() => setShowAdmin(false)}
        onSaved={() => { api.get("/config").then(({ data }) => setConfig(data)).catch(() => {}); loadUsage(); }} />
    </div>
  );
}
